// ─────────────────────────────────────────────────────────────────────────────
// inspectionAdminService.js  —  Inspection Hierarchy CRUD (Admin / Form Design)
//
// Endpoint kökü: /api/v1/inspection
// Tüm endpoint'ler session cookie (cookie-based auth) gerektirir.
//
// Desteklenen level URL segmentleri:
//   departments | scopes | types | categories | questions |
//   hazards | risks | nonconformities | legal-procedures | suggestions
//
// Bölüm referansları (Project_Bible.agent.md):
//   Bölüm 4    → CRUD (create, list, get, getTexts, update, softDelete, hardDelete)
//   Bölüm 5    → getLevels (static 1/2/3)
//   Bölüm 6    → Legal Procedure junction NC seviyesi (list, link, unlink) — DEPRECATED
//   Bölüm 7    → Reorder (categories, questions)
//   Bölüm 8    → Bulk Photo Mode
//   Bölüm 24.1 → Suggestions CRUD
//   Bölüm 24.2 → Hazard ↔ Suggestions junction
//   Bölüm 24.3 → Hazard ↔ Legal Procedures junction (YENİ — hazard seviyesine taşındı)
//   Bölüm 24.4 → Generic Descendant Listing
//   Bölüm 24.5 → Copy Subtree
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/inspection`;

// ── Backend raw-array workaround ──────────────────────────────────────────────
// Backend native SQL sorgusu Object[] (raw array) döndürüyor.
// Jackson bunu [[id, name, ...]] şeklinde serialize ediyor.
// Her level için kolon sırası dökümanın SELECT m.* sırasını takip eder.
// Backend düzeltilince Array.isArray kontrolü devreye girmez, obje olarak geçer.
const ARRAY_NORMALIZERS = {
  'departments': (a) => ({
    inspection_department_id:          a[0],
    inspection_department_name:        a[1],
    inspection_department_description: a[2],
    is_deleted:                        a[3],
    translated_name:                   a[4] ?? a[1],
    display_name:                      a[5] ?? null,
    child_count:                       a[6] ?? 0,
  }),
  'scopes': (a) => {
    // April 2026: son iki eleman display_name (string|null) ve child_count (number)
    // translated_name: a[len-3] — null ise a[2] (scope_name) fallback'i kullan
    // ⚠️ trIdx fallback a[len-2]'ye (display_name) DÜŞMEMELİ
    const len = a.length;
    const child_count  = (typeof a[len - 1] === 'number') ? a[len - 1] : 0;
    const display_name = a[len - 2] ?? null;
    const tr = (typeof a[len - 3] === 'string' && a[len - 3].trim()) ? a[len - 3] : null;

    const deptAtPos1 = typeof a[1] !== 'string';
    if (deptAtPos1) {
      return {
        inspection_scope_id:          a[0],
        inspection_department_id:     a[1] ?? null,
        inspection_scope_name:        a[2],
        inspection_scope_description: a[3],
        target_entity:                a[4],
        is_deleted:                   a[5],
        translated_name:              tr ?? a[2],
        display_name,
        child_count,
      };
    }
    return {
      inspection_scope_id:          a[0],
      inspection_scope_name:        a[1],
      inspection_scope_description: a[2],
      target_entity:                a[3],
      is_deleted:                   a[4],
      inspection_department_id:     a.length >= 7 ? (a[5] ?? null) : null,
      translated_name:              tr ?? a[1],
      display_name,
      child_count,
    };
  },
  'types': (a) => ({
    inspection_type_id:          a[0],
    inspection_scope_id:         a[1],
    inspection_type_name:        a[2],
    inspection_type_short:       a[3],
    main_question:               a[4],
    main_question_quest_type:    a[5],
    is_deleted:                  a[6],
    translated_name:             a[7] ?? a[2],
    translated_short:            a[8] ?? a[3],
    display_name:                a[9] ?? null,
    child_count:                 a[10] ?? 0,
  }),
  'categories': (a) => ({
    inspection_category_id:          a[0],
    inspection_type_id:              a[1],
    inspection_category_name:        a[2],
    inspection_category_description: a[3],
    is_deleted:                      a[4],
    sort_order:                      a[5] ?? null,   // reorder endpoint sonrası eklendi
    translated_name:                 a[6] ?? a[2],   // a[5]'ten a[6]'ya kaydı
    display_name:                    a[7] ?? null,
    child_count:                     a[8] ?? 0,
  }),
  'questions': (a) => ({
    inspection_question_id:            a[0],
    inspection_category_id:            a[1],
    inspection_question_name:          a[2],
    inspection_question_description:   a[3],
    quest_type:                        a[4],
    // a[5] eski is_photo_required (backward compat)
    isPhotoRequiredPositive:           a[6] ?? 0,
    isPhotoRequiredNeutral:            a[7] ?? 0,
    sort_order:                        a[8] ?? null,  // reorder endpoint sonrası eklendi
    is_deleted:                        a[9],          // a[8]'den a[9]'a kaydı
    translated_name:                   a[10] ?? a[2], // a[9]'dan a[10]'a kaydı
    display_name:                      a[11] ?? null,
    child_count:                       a[12] ?? 0,
  }),
  'hazards': (a) => ({
    inspection_hazard_id:          a[0],
    inspection_question_id:        a[1] ?? null,
    inspection_hazard_name:        a[2],
    inspection_hazard_description: a[3],
    is_deleted:                    a[4],
    translated_name:               a[5] ?? a[2],
    display_name:                  a[6] ?? null,
    child_count:                   a[7] ?? 0,
  }),
  'risks': (a) => ({
    inspection_risk_id:          a[0],
    inspection_hazard_id:        a[1],
    inspection_risk_name:        a[2],
    inspection_risk_description: a[3],
    is_deleted:                  a[4],
    translated_name:             a[5] ?? a[2],
    display_name:                a[6] ?? null,
    child_count:                 a[7] ?? 0,
  }),
  'nonconformities': (a) => ({
    inspection_nonconformity_id:          a[0],
    inspection_risk_id:                   a[1],
    inspection_nonconformity_name:        a[2],
    inspection_nonconformity_description: a[3],
    olasilik:                             a[4],
    frekans:                              a[5],
    siddet:                               a[6],
    risk_degeri:                          a[7],
    oneri:                                a[8],
    is_deleted:                           a[9],
    translated_name:                      a[10] ?? a[2],
    translated_description:               a[11] ?? a[3],
    translated_oneri:                     a[12] ?? a[8],
    display_name:                         a[13] ?? null,
    child_count:                          a[14] ?? 0,
  }),
  'legal-procedures': (a) => ({
    inspection_legal_procedure_id:          a[0],
    inspection_legal_procedure_name:        a[1],
    inspection_legal_procedure_description: a[2],
    is_deleted:                             a[3],
    translated_name:                        a[4] ?? a[1],
    display_name:                           a[5] ?? null,
    child_count:                            a[6] ?? 0,
  }),
  // Bölüm 24.1 — Suggestions (bağımsız havuz, parentId yok)
  'suggestions': (a) => ({
    inspection_suggestion_id:          a[0],
    inspection_suggestion_name:        a[1],
    inspection_suggestion_description: a[2],
    is_deleted:                        a[3],
    translated_name:                   a[4] ?? a[1],
    display_name:                      a[5] ?? null,
    child_count:                       a[6] ?? 0,
  }),
};

export function normalizeItem(level, item) {
  if (!item) return item;

  if (Array.isArray(item)) {
    const fn = ARRAY_NORMALIZERS[level];
    return fn ? fn(item) : item;
  }

  // Object response — display_name + child_count her seviyede garantile
  // questions seviyesi için ek snake_case → camelCase dönüşümü
  const base = {
    ...item,
    display_name: item.display_name ?? null,
    child_count:  item.child_count  ?? 0,
  };

  if (level === 'questions') {
    return {
      ...base,
      // Backend object response: is_photo_required_positive (snake_case)
      // Array normalizer sonrası: isPhotoRequiredPositive (camelCase)
      // Her iki kaynağı da destekle
      isPhotoRequiredPositive: base.isPhotoRequiredPositive ?? base.is_photo_required_positive ?? 0,
      isPhotoRequiredNeutral:  base.isPhotoRequiredNeutral  ?? base.is_photo_required_neutral  ?? 0,
    };
  }

  return base;
}

// Geçerli level segmentleri (validation için)
export const LEVELS = [
  'departments',
  'scopes',
  'types',
  'categories',
  'questions',
  'hazards',
  'risks',
  'nonconformities',
  'legal-procedures',
  'suggestions',          // Bölüm 24.1 — bağımsız havuz, copy/reorder dışında tam CRUD
];

// ── 4.1 CREATE — POST /{level} ─────────────────────────────────────────────────
/**
 * Hiyerarşide yeni bir öğe oluşturur.
 * @param {string} level - Hedef level (bkz. LEVELS sabiti)
 * @param {Object} data  - Body alanları level'e göre değişir (bkz. endpoint guide § 4.1)
 * @returns {{ result: boolean, msg: string, data: number }} - data = yeni öğenin ID'si
 *
 * Örnek — yeni tip oluştur:
 *   createItem('types', { parentId: 1, name: 'İSG Denetim', shortName: 'ISG', mainQuestion: '...' })
 */
export async function createItem(level, data) {
  const res = await axios.post(`${BASE}/${level}`, data, { headers: getAuthHeader() });
  return res.data;
}

// ── 4.2 LIST — GET /{level}?parentId=&languageId= ─────────────────────────────
/**
 * Belirtilen level'daki öğeleri listeler. Soft-deleted öğeler döndürülmez.
 * @param {string} level
 * @param {{ parentId?: number, languageId?: number }} params
 *   - parentId: types/categories/questions/hazards/risks/nonconformities için zorunlu
 *   - scopes ve legal-procedures için parentId gönderilmez (ignored)
 * @returns {Array}
 *
 * Örnek — ISG tipin kategorilerini getir:
 *   listItems('categories', { parentId: 1, languageId: 1 })
 */
export async function listItems(level, { parentId, languageId = 1 } = {}) {
  const q = new URLSearchParams({ languageId });
  if (parentId != null) q.set('parentId', String(parentId));
  const res = await axios.get(`${BASE}/${level}?${q}`, { headers: getAuthHeader() });
  return (res.data || []).map((item) => normalizeItem(level, item));
}

// ── 4.3 GET SINGLE — GET /{level}/{id}?languageId= ────────────────────────────
/**
 * ID ile tek öğe getirir (çeviri dahil).
 * @param {string} level
 * @param {number} id
 * @param {{ languageId?: number }} params
 * @returns {Object|null}
 */
export async function getItem(level, id, { languageId = 1 } = {}) {
  const res = await axios.get(
    `${BASE}/${level}/${id}?languageId=${languageId}`,
    { headers: getAuthHeader() }
  );
  return normalizeItem(level, res.data) || null;
}

// ── 4.4 GET ALL TRANSLATIONS — GET /{level}/{id}/texts ────────────────────────
/**
 * Bir öğenin tüm dillerdeki çevirilerini getirir.
 * Düzenleme formunda Türkçe + İngilizce alanları göstermek için kullanılır.
 * @param {string} level
 * @param {number} id
 * @returns {Array}
 *
 * Örnek yanıt (categories):
 *   [{ id: 15, inspection_category_id: 10, inspection_category_name: 'KKD', language_id: 1 }, ...]
 */
export async function getItemTexts(level, id) {
  const res = await axios.get(`${BASE}/${level}/${id}/texts`, { headers: getAuthHeader() });
  return res.data || [];
}

// ── 4.5 UPDATE — PUT /{level}/{id} ────────────────────────────────────────────
/**
 * Mevcut öğeyi günceller. Sadece gönderilen alanlar değişir.
 * translations dizisi varsa: mevcut çeviriler silinir, yenileri eklenir (full replace).
 * translations yoksa: mevcut çeviriler dokunulmaz.
 * @param {string} level
 * @param {number} id
 * @param {Object} data
 * @returns {{ result: boolean, msg: string }}
 */
export async function updateItem(level, id, data) {
  const res = await axios.put(`${BASE}/${level}/${id}`, data, { headers: getAuthHeader() });
  return res.data;
}

// ── 4.6 SOFT DELETE — DELETE /{level}/{id} ────────────────────────────────────
/**
 * Öğeyi soft-delete yapar (is_deleted=1). Güvenli silme.
 * Soft-deleted öğeler list ve generate endpoint'lerinde görünmez,
 * ancak daha önce doldurulmuş formların referansları korunur.
 * @param {string} level
 * @param {number} id
 * @returns {{ result: boolean, msg: string }}
 */
export async function softDeleteItem(level, id) {
  const res = await axios.delete(`${BASE}/${level}/${id}`, { headers: getAuthHeader() });
  return res.data;
}

// ── 4.7 HARD DELETE — DELETE /{level}/{id}/hard ───────────────────────────────
/**
 * Öğeyi ve çevirilerini fiziksel olarak siler. GERİ ALINAMAZ.
 * Çocuk öğeleri varsa backend reddeder (önce çocukları silin).
 * @param {string} level
 * @param {number} id
 * @returns {{ result: boolean, msg: string }}
 */
export async function hardDeleteItem(level, id) {
  const res = await axios.delete(`${BASE}/${level}/${id}/hard`, { headers: getAuthHeader() });
  return res.data;
}

// ── 5. LEVELS — GET /levels ───────────────────────────────────────────────────
/**
 * Statik denetim seviyelerini döndürür.
 * @returns {[{ inspection_level_id: number, inspection_level_name: string }]}
 *
 * Sabit 3 satır:
 *   1 → Denetim Raporu
 *   2 → Uygunsuzluk Formu
 *   3 → Kapalı DÖF (DÖF Formu)
 */
export async function getLevels() {
  const res = await axios.get(`${BASE}/levels`, { headers: getAuthHeader() });
  return res.data || [];
}

// ── 6.1 LIST LEGAL PROCEDURES for NC ─────────────────────────────────────────
/**
 * Bir uygunsuzluğa bağlı yasal prosedürleri listeler.
 * @param {number} ncId - Nonconformity ID
 * @param {{ languageId?: number }} params
 * @returns {Array}
 */
export async function getLegalProceduresForNc(ncId, { languageId = 1 } = {}) {
  const res = await axios.get(
    `${BASE}/nonconformities/${ncId}/legal-procedures?languageId=${languageId}`,
    { headers: getAuthHeader() }
  );
  return (res.data || []).map((item) => normalizeItem('legal-procedures', item));
}

// ── 6.2 LINK legal procedure → NC ────────────────────────────────────────────
/**
 * Bir yasal prosedürü uygunsuzluğa bağlar (many-to-many junction).
 * @param {number} ncId
 * @param {number} legalProcedureId
 * @returns {{ result: boolean, msg: string }}
 */
export async function linkLegalProcedure(ncId, legalProcedureId) {
  const res = await axios.post(
    `${BASE}/nonconformities/${ncId}/legal-procedures`,
    { legalProcedureId },
    { headers: getAuthHeader() }
  );
  return res.data;
}

// ── 6.3 UNLINK legal procedure from NC ───────────────────────────────────────
/**
 * Yasal prosedür bağını kaldırır.
 * @param {number} ncId
 * @param {number} lpId - Legal Procedure ID
 * @returns {{ result: boolean, msg: string }}
 */
export async function unlinkLegalProcedure(ncId, lpId) {
  const res = await axios.delete(
    `${BASE}/nonconformities/${ncId}/legal-procedures/${lpId}`,
    { headers: getAuthHeader() }
  );
  return res.data;
}

// ── NEW: Risk Suggester — GET /hazards/{hazardId}/nonconformities ─────────────
/**
 * Belirtilen hazard (Uygunsuzluk) altındaki TÜM nonconformity'leri (Risk) döndürür.
 * Hazard → Risk (Tehlike) → Nonconformity (Risk) zincirini yürüyerek flat liste döner.
 * Risk Suggester özelliği için özel olarak eklenmiştir.
 *
 * @param {number} hazardId - Uygunsuzluk ID'si (path[0].id)
 * @param {{ languageId?: number }} params
 * @returns {Array<{
 *   inspection_nonconformity_id: number,
 *   nonconformity_name: string|null,
 *   olasilik: number, frekans: number, siddet: number,
 *   risk_degeri: number, oneri: string|null,
 *   risk_name: string|null
 * }>}
 */
export async function getHazardNonconformities(hazardId, { languageId = 1 } = {}) {
  const url = `${BASE}/hazards/${hazardId}/nonconformities?languageId=${languageId}`;
  const res = await axios.get(url, { headers: getAuthHeader() });

  // Backend raw array döndürüyor: [Array(8), Array(8), ...]
  // Sütun sırası (addendum'a göre):
  //   a[0] inspection_nonconformity_id
  //   a[1] nonconformity_name
  //   a[2] olasilik
  //   a[3] frekans
  //   a[4] siddet
  //   a[5] risk_degeri
  //   a[6] oneri
  //   a[7] risk_name
  const raw = Array.isArray(res.data?.data) ? res.data.data
            : Array.isArray(res.data)       ? res.data
            : [];

  return raw.map((a) => Array.isArray(a) ? {
    inspection_nonconformity_id: a[0],
    nonconformity_name:          a[1] ?? null,
    olasilik:                    a[2],
    frekans:                     a[3],
    siddet:                      a[4],
    risk_degeri:                 a[5],
    oneri:                       a[6] ?? null,
    risk_name:                   a[7] ?? null,
  } : a);
}

// ── 7. REORDER — PUT /{level}/reorder ────────────────────────────────────────
/**
 * Bir seviyedeki öğelerin sırasını günceller.
 * @param {'categories'|'questions'} level  — sadece bu iki seviye destekleniyor
 * @param {number[]} orderedIds             — tüm kardeş ID'leri yeni sırada (eksiksiz!)
 * @returns {{ result: boolean, message: string, data: null }}
 *
 * ⚠️ Gönderilmeyen ID'lerin sort_order değeri değişmez → gap/çakışma oluşabilir.
 *    Her zaman aynı parent altındaki TÜM kardeş ID'leri gönder.
 */
export async function reorderItems(level, orderedIds) {
  const res = await axios.put(
    `${BASE}/${level}/reorder`,
    orderedIds,
    { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
  );
  return res.data; // { result, message, data }
}

// ── 8. BULK PHOTO MODE — PUT /categories/{id}/questions/photo-mode ──────────
/**
 * Kategori altındaki TÜM soruların fotoğraf modunu toplu günceller.
 * @param {number} categoryId
 * @param {"positiveOnly"|"neutralOnly"|"both"|"none"} mode
 * @returns {{ result: boolean, msg: string }}
 *
 * mode → isPhotoRequiredPositive | isPhotoRequiredNeutral
 *  positiveOnly → 1 | 0
 *  neutralOnly  → 0 | 1
 *  both         → 1 | 1
 *  none         → 0 | 0
 *
 * ⚠️ Geri alınamaz — tüm soruları ezip yazar. UI'da onay dialog'u zorunlu.
 */
export async function bulkSetPhotoMode(categoryId, mode) {
  const res = await axios.put(
    `${BASE}/categories/${categoryId}/questions/photo-mode`,
    { mode },
    { headers: getAuthHeader() }
  );
  return res.data;
}

// ── Yardımcı: tüm hierarchy için tam ağaç çek (scope→type→cat→question) ──────
/**
 * Admin panel için: kapsam seç → tipler listele, her tip için ağacı getir.
 * Bu fonksiyon birden fazla API çağrısı yapar.
 * @param {number} typeId
 * @param {number} languageId
 * @returns {{ type, categories: [{ category, questions }] }}
 */
export async function getTypeWithHierarchy(typeId, languageId = 1) {
  const [typeData, cats] = await Promise.all([
    getItem('types', typeId, { languageId }),
    listItems('categories', { parentId: typeId, languageId }),
  ]);
  const questionsPerCat = await Promise.all(
    cats.map((cat) =>
      listItems('questions', { parentId: cat.inspection_category_id, languageId })
    )
  );
  return {
    type: typeData,
    categories: cats.map((cat, i) => ({
      category: cat,
      questions: questionsPerCat[i] || [],
    })),
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// Bölüm 24.1 — SUGGESTIONS CRUD
// Endpoint: /api/v1/inspection/suggestions
// Bağımsız havuz — parentId YOK. Hazard'lara junction tablosu ile bağlanır.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Tüm öneri kayıtlarını listeler.
 * @param {{ languageId?: number }} params
 * @returns {Array}
 */
export async function getSuggestions({ languageId = 1 } = {}) {
  const res = await axios.get(
    `${BASE}/suggestions?languageId=${languageId}`,
    { headers: getAuthHeader() }
  );
  const raw = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
  return raw.map((item) => normalizeItem('suggestions', item));
}

/**
 * Tek öneri kaydını getirir.
 * @param {number} id
 * @param {{ languageId?: number }} params
 * @returns {Object}
 */
export async function getSuggestion(id, { languageId = 1 } = {}) {
  const res = await axios.get(
    `${BASE}/suggestions/${id}?languageId=${languageId}`,
    { headers: getAuthHeader() }
  );
  return normalizeItem('suggestions', res.data);
}

/**
 * Bir öneri kaydının tüm çeviri satırlarını getirir.
 * @param {number} id
 * @returns {Array}
 */
export async function getSuggestionTexts(id) {
  const res = await axios.get(
    `${BASE}/suggestions/${id}/texts`,
    { headers: getAuthHeader() }
  );
  return res.data || [];
}

/**
 * Yeni öneri oluşturur.
 * @param {{ name: string, description?: string, translations?: Array }} body
 * @returns {{ result: boolean, message: string, data: number }} — data = yeni ID
 */
export async function createSuggestion(body) {
  const res = await axios.post(
    `${BASE}/suggestions`,
    body,
    { headers: getAuthHeader() }
  );
  return res.data;
}

/**
 * Öneri günceller (partial — sadece gönderilen key'ler güncellenir).
 * @param {number} id
 * @param {Object} body
 * @returns {{ result: boolean, message: string, data: null }}
 */
export async function updateSuggestion(id, body) {
  const res = await axios.put(
    `${BASE}/suggestions/${id}`,
    body,
    { headers: getAuthHeader() }
  );
  return res.data;
}

/**
 * Öneriyi soft-delete yapar (is_deleted = 1).
 * @param {number} id
 * @returns {{ result: boolean, message: string, data: null }}
 */
export async function deleteSuggestion(id) {
  const res = await axios.delete(
    `${BASE}/suggestions/${id}`,
    { headers: getAuthHeader() }
  );
  return res.data;
}

/**
 * Öneriyi kalıcı olarak siler. GERİ ALINAMAZ — onay dialogu zorunlu.
 * @param {number} id
 * @returns {{ result: boolean, message: string, data: null }}
 */
export async function hardDeleteSuggestion(id) {
  const res = await axios.delete(
    `${BASE}/suggestions/${id}/hard`,
    { headers: getAuthHeader() }
  );
  return res.data;
}

// ═════════════════════════════════════════════════════════════════════════════
// Bölüm 24.2 — HAZARD ↔ SUGGESTIONS JUNCTION
// Tablo: inspection_hazard_to_suggestion
// Endpoint: /api/v1/inspection/hazards/{hazardId}/suggestions
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Bir hazard'a bağlı tüm önerileri listeler.
 * @param {number} hazardId
 * @param {{ languageId?: number }} params
 * @returns {Array}
 */
export async function getSuggestionsForHazard(hazardId, { languageId = 1 } = {}) {
  const res = await axios.get(
    `${BASE}/hazards/${hazardId}/suggestions?languageId=${languageId}`,
    { headers: getAuthHeader() }
  );
  const raw = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
  return raw.map((item) => normalizeItem('suggestions', item));
}

/**
 * Bir öneriyi hazard'a bağlar.
 * @param {number} hazardId
 * @param {number} suggestionId
 * @returns {{ result: boolean, message: string, data: null }}
 * 400 → ID bulunamadı veya bağlantı zaten mevcut.
 */
export async function linkSuggestionToHazard(hazardId, suggestionId) {
  const res = await axios.post(
    `${BASE}/hazards/${hazardId}/suggestions`,
    { suggestionId },
    { headers: getAuthHeader() }
  );
  return res.data;
}

/**
 * Hazard'dan bir öneri bağını koparır.
 * @param {number} hazardId
 * @param {number} suggestionId
 * @returns {{ result: boolean, message: string, data: null }}
 * 400 → bağlantı zaten yoktu.
 */
export async function unlinkSuggestionFromHazard(hazardId, suggestionId) {
  const res = await axios.delete(
    `${BASE}/hazards/${hazardId}/suggestions/${suggestionId}`,
    { headers: getAuthHeader() }
  );
  return res.data;
}

// ═════════════════════════════════════════════════════════════════════════════
// Bölüm 24.3 — HAZARD ↔ LEGAL PROCEDURES JUNCTION (YENİ — hazard seviyesi)
// Tablo: inspection_hazard_to_legal_procedure
// Endpoint: /api/v1/inspection/hazards/{hazardId}/legal-procedures
//
// NOT: Eski NC seviyesi junction (Bölüm 6) deprecated ama hâlâ çalışıyor.
//      Yeni UI için bu fonksiyonları kullan.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Bir hazard'a bağlı tüm yasal prosedürleri listeler.
 * @param {number} hazardId
 * @param {{ languageId?: number }} params
 * @returns {Array}
 */
export async function getLegalProceduresForHazard(hazardId, { languageId = 1 } = {}) {
  const res = await axios.get(
    `${BASE}/hazards/${hazardId}/legal-procedures?languageId=${languageId}`,
    { headers: getAuthHeader() }
  );
  const raw = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
  return raw.map((item) => normalizeItem('legal-procedures', item));
}

/**
 * Bir yasal prosedürü hazard'a bağlar.
 * @param {number} hazardId
 * @param {number} legalProcedureId
 * @returns {{ result: boolean, message: string, data: null }}
 */
export async function linkLegalProcedureToHazard(hazardId, legalProcedureId) {
  const res = await axios.post(
    `${BASE}/hazards/${hazardId}/legal-procedures`,
    { legalProcedureId },
    { headers: getAuthHeader() }
  );
  return res.data;
}

/**
 * Hazard'dan bir yasal prosedür bağını koparır.
 * @param {number} hazardId
 * @param {number} lpId
 * @returns {{ result: boolean, message: string, data: null }}
 */
export async function unlinkLegalProcedureFromHazard(hazardId, lpId) {
  const res = await axios.delete(
    `${BASE}/hazards/${hazardId}/legal-procedures/${lpId}`,
    { headers: getAuthHeader() }
  );
  return res.data;
}

// ═════════════════════════════════════════════════════════════════════════════
// Bölüm 24.4 — GENERIC DESCENDANT LISTING
// Endpoint: GET /api/v1/inspection/{ancestorLevel}/{ancestorId}/{descendantLevel}
//
// Desteklenen ana zincir seviyeleri:
//   departments → scopes → types → categories → questions →
//   hazards → risks → nonconformities
// suggestions ve legal-procedures → 400 döner.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Bir ancestor node'un altındaki tüm torunları flat liste olarak döndürür.
 * Çok adımlı zincir çağrıları yerine tek istekle performanslı listeleme.
 *
 * @param {string} ancestorLevel  — ör. 'departments', 'types', 'categories'
 * @param {number} ancestorId
 * @param {string} descendantLevel — ancestor'dan aşağıda olmalı
 * @param {number} languageId
 * @returns {Array} — descendantLevel'a göre normalize edilmiş flat liste
 *
 * @example
 * // Bir kategori altındaki tüm hazardları getir
 * getDescendants('categories', 12, 'hazards')
 *
 * @example
 * // Bir birim altındaki tüm riskleri getir
 * getDescendants('departments', 5, 'nonconformities')
 */
export async function getDescendants(ancestorLevel, ancestorId, descendantLevel, languageId = 1) {
  const res = await axios.get(
    `${BASE}/${ancestorLevel}/${ancestorId}/${descendantLevel}?languageId=${languageId}`,
    { headers: getAuthHeader() }
  );
  // Backend GenericResultJson döndürebilir veya raw array
  const raw = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
  return raw.map((item) => normalizeItem(descendantLevel, item));
}

// ═════════════════════════════════════════════════════════════════════════════
// Bölüm 24.5 — COPY SUBTREE
// Endpoint: POST /api/v1/inspection/{level}/{sourceId}/copy
//
// Bir node'u ve tüm alt ağacını (çeviriler + junction'lar dahil) derin kopyalar.
// GERİ ALINAMAZ — UI'da her zaman onay dialogu göster.
//
// Desteklenmeyen seviyeleri (suggestions, legal-procedures) → 400 döner.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Bir node'u ve tüm alt ağacını yeni bir parent'a derin kopyalar.
 *
 * @param {string} level      — kopyalanacak node'un seviyesi (ör. 'categories', 'hazards')
 * @param {number} sourceId   — kopyalanacak node'un ID'si
 * @param {number|null} targetParentId — hedef parent ID (departments için null olabilir)
 * @returns {{ result: boolean, message: string, data: { newRootId: number, copiedCount: number } }}
 *
 * ⚠️ targetParentId, kaynak node'dan TAM BİR SEVİYE üstündeki ID olmalı.
 * ⚠️ GERİ ALINAMAZ — her zaman onay dialogu göster, copiedCount bilgisini kullanıcıya ilet.
 */
export async function copySubtree(level, sourceId, targetParentId) {
  const body = targetParentId != null ? { targetParentId } : {};
  const res = await axios.post(
    `${BASE}/${level}/${sourceId}/copy`,
    body,
    { headers: getAuthHeader() }
  );
  return res.data; // { result, message, data: { newRootId, copiedCount } }
}
